/**
 * CollapsiblePanel - Shared panel wrapper for board sidebar panels
 */

import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, X, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface CollapsiblePanelProps {
  side: 'left' | 'right';
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  actions?: ReactNode;
}

export default function CollapsiblePanel({
  side,
  isOpen,
  onToggle,
  onClose,
  title,
  icon: Icon,
  children,
  actions,
}: CollapsiblePanelProps) {

  return (
    <>
      {/* Collapsed tab (shown when panel is closed) */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className={clsx(
            'fixed top-1/2 z-30 -translate-y-1/2',
            'hidden sm:flex items-center justify-center',
            'h-24 w-8 rounded-md',
            'bg-white dark:bg-neutral-800',
            'border border-neutral-200 dark:border-neutral-700',
            'shadow-md hover:shadow-lg',
            'text-neutral-600 hover:text-neutral-900',
            'dark:text-neutral-400 dark:hover:text-neutral-100',
            'transition-all duration-200',
            side === 'left' ? 'left-0 rounded-l-none border-l-0' : 'right-0 rounded-r-none border-r-0'
          )}
          title={`Open ${title}`}
        >
          <div className="flex flex-col items-center gap-1">
            <Icon className="h-4 w-4" />
            {side === 'left' ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </div>
        </button>
      )}

      {/* Panel (shown when open) */}
      <div
        className={clsx(
          'fixed top-[4.5rem] z-40 w-80',
          'h-[calc(100vh-4.5rem)]', // Full height minus header
          'flex flex-col',
          'bg-white dark:bg-neutral-900',
          'border-neutral-200 dark:border-neutral-700',
          'shadow-xl',
          'transition-transform duration-200 ease-out',
          side === 'left' ? 'left-0 border-r' : 'right-0 border-l',
          isOpen
            ? 'translate-x-0'
            : side === 'left'
              ? '-translate-x-full'
              : 'translate-x-full'
        )}
      >
        {/* Panel Header */}
        <div
          className={clsx(
            'flex h-14 shrink-0 items-center justify-between',
            'border-b border-neutral-200 dark:border-neutral-700',
            'px-4'
          )}
        >
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {title}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {actions}
            <button
              onClick={onClose}
              className={clsx(
                'rounded p-1.5',
                'text-neutral-500 hover:text-neutral-700',
                'dark:text-neutral-400 dark:hover:text-neutral-200',
                'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                'transition-colors'
              )}
              title={`Close ${title}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>

      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className={clsx(
            'fixed inset-0 z-30',
            'bg-black/20 dark:bg-black/40',
            'md:hidden',
            'transition-opacity duration-200'
          )}
          onClick={onClose}
        />
      )}
    </>
  );
}
